import React from 'react';
import { Typography, theme } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconArrowLeft } from '@tabler/icons-react';
import dayjs from 'dayjs';
import NotFound from '../NotFound/NotFound';
import { LEGAL_DOCUMENTS } from '../../constants/legalDocuments';
import { LEGAL_ENTITY, IS_LEGAL_ENTITY_IDENTIFIED } from '../../constants/legalEntity';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { SURFACE_1_SOFT, TEAL } from '../../theme/creamyTokens';

const { Title, Paragraph, Text } = Typography;

interface LegalDocumentPageProps {
  /** Key into LEGAL_DOCUMENTS. */
  slug: string;
}

/**
 * Renders one LEGAL_DOCUMENTS entry: a title, a date, and a run of headed
 * paragraphs.
 *
 * Deliberately standalone rather than wrapped in LandingLayout or AppLayout.
 * Its whole audience is somebody who tapped "política de privacidad" from
 * the pre-launch form's consent checkbox, in a new tab, while the rest of
 * the app is hidden behind landing mode -- putting the app's own navigation
 * around it would show them a product they cannot reach yet.
 *
 * Long-form text, so the column is capped at a readable measure and the type
 * is a size up from the app's default. Everything else is the same creamy
 * surface as the page that linked here.
 */
const LegalDocumentPage: React.FC<LegalDocumentPageProps> = ({ slug }) => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { token } = theme.useToken();

  const document = LEGAL_DOCUMENTS[slug];
  if (!document) return <NotFound />;

  // Every placeholder any of these paragraphs might use, passed to all of
  // them: i18next ignores the ones a given string does not mention, and a
  // per-paragraph params map would be one more thing to keep in step with
  // the text.
  const params = {
    email: LEGAL_ENTITY.privacyEmail,
    hosting: LEGAL_ENTITY.hostingProvider,
    identity: [LEGAL_ENTITY.name, LEGAL_ENTITY.taxId, LEGAL_ENTITY.address].filter(Boolean).join(' · '),
  };

  return (
    <div style={{ minHeight: '100vh', background: SURFACE_1_SOFT }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: isMobile ? '24px 20px 56px' : '48px 24px 80px' }}>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: TEAL, fontSize: 14 }}>
          <IconArrowLeft size={16} />
          {translate(t, TRANSLATIONS.GOHOME)}
        </Link>

        <Title level={2} style={{ marginTop: 18, marginBottom: 4, fontSize: isMobile ? 26 : 32 }}>
          {translate(t, document.titleKey)}
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {translate(t, TRANSLATIONS.LEGAL_UPDATED_AT, { date: dayjs(document.updatedAt).format('D MMMM YYYY') })}
        </Text>

        {document.introKey && (
          <Paragraph style={{ marginTop: 18, fontSize: 15, color: token.colorTextSecondary }}>
            {translate(t, document.introKey, params)}
          </Paragraph>
        )}

        {document.sections.map((section) => (
          <section key={section.headingKey} style={{ marginTop: 28 }}>
            <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>
              {translate(t, section.headingKey)}
            </Title>
            {/* The one paragraph that is not in the document data: who the
                controller is. It is only printed once somebody has filled
                LEGAL_ENTITY in -- an empty "· ·" line would be worse than
                no line, and inventing a name would be worse than both. */}
            {section.headingKey === TRANSLATIONS.PRIVACY_LANDING_CONTROLLER_HEADING && IS_LEGAL_ENTITY_IDENTIFIED && (
              <Paragraph style={{ fontSize: 15 }}>
                {translate(t, TRANSLATIONS.PRIVACY_LANDING_CONTROLLER_BODY, params)}
              </Paragraph>
            )}
            {section.bodyKeys.map((bodyKey) => (
              <Paragraph key={bodyKey} style={{ fontSize: 15 }}>
                {translate(t, bodyKey, params)}
              </Paragraph>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
};

export default LegalDocumentPage;
